import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from './ui/sonner';
import axios from 'axios';
import { API_URL, getAuthHeaders } from '../utils/auth';

/**
 * PhoneCaptureModal - Post-login phone number capture gate
 *
 * v2.34.0 - EazyLink requirement
 * Shows on first login if phone_e164 is null/empty
 * Allows skip (up to 3 times), then shows persistent banner
 */
const PhoneCaptureModal = ({ user, onComplete, tenantConfig }) => {
  const [phoneCountryCode, setPhoneCountryCode] = useState('+91');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Set default country code from tenant config
    if (tenantConfig?.quick_profile?.default_country_code) {
      setPhoneCountryCode(tenantConfig.quick_profile.default_country_code);
    }
  }, [tenantConfig]);

  const handleSave = async () => {
    // Validate phone number
    if (!phoneNumber.trim()) {
      toast.error('Please enter your phone number');
      return;
    }

    const cleanNumber = phoneNumber.replace(/\D/g, '');
    if (cleanNumber.length < 7 || cleanNumber.length > 15) {
      toast.error('Please enter a valid phone number (7-15 digits)');
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/student/profile`,
        {
          phone_country_code: phoneCountryCode,
          phone_number: cleanNumber,
        },
        { headers: getAuthHeaders() }
      );

      toast.success('Phone number saved!');
      onComplete();
    } catch (error) {
      console.error('Error saving phone:', error);
      toast.error('Failed to save phone number. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    // Increment skip counter in localStorage
    const currentCount = parseInt(localStorage.getItem('phone_capture_skip_count') || '0');
    localStorage.setItem('phone_capture_skip_count', String(currentCount + 1));
    onComplete();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-6 relative animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-sky-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">👋</span>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            Welcome! One quick thing...
          </h2>
          <p className="text-slate-600 dark:text-slate-300">
            Your counsellor needs your phone number to help you with program shortlisting.
          </p>
        </div>

        {/* Phone Input */}
        <div className="space-y-4 mb-6">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Phone Number
          </label>
          <div className="flex gap-2">
            {/* Country Code Picker */}
            <select
              value={phoneCountryCode}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'other') {
                  const customCode = prompt('Enter country code (e.g., +880 for Bangladesh):');
                  if (customCode && customCode.startsWith('+')) {
                    setPhoneCountryCode(customCode);
                  }
                } else {
                  setPhoneCountryCode(value);
                }
              }}
              className="w-32 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            >
              <option value="+91">India (+91)</option>
              <option value="+61">Australia (+61)</option>
              <option value="+1">Canada (+1)</option>
              <option value="+49">Germany (+49)</option>
              <option value="+353">Ireland (+353)</option>
              <option value="+234">Nigeria (+234)</option>
              <option value="+65">Singapore (+65)</option>
              <option value="+971">UAE (+971)</option>
              <option value="+44">UK (+44)</option>
              <option value="+1">USA (+1)</option>
              <option value="other">Other...</option>
            </select>

            <Input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="9876543210"
              className="flex-1"
              autoFocus
            />
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-sky-500 hover:bg-sky-600 text-white font-medium py-3 rounded-lg"
          >
            {saving ? 'Saving...' : 'Continue →'}
          </Button>

          <button
            onClick={handleSkip}
            disabled={saving}
            className="w-full text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhoneCaptureModal;
